import { useEffect, useRef } from "react";

/**
 * Lightweight canvas-based molecular network background.
 * - Particles drift slowly; lines connect particles within a max distance.
 * - Mouse interaction creates a soft attraction radius.
 * - Devicepixelratio-aware. Pauses when tab hidden.
 */
export default function MolecularBackground({ density = 1 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let dpr = Math.max(window.devicePixelRatio || 1, 1);
    const particles = [];
    const mouse = { x: -9999, y: -9999, active: false };

    const COLORS = {
      node: "rgba(0, 122, 138, 0.85)",
      nodeCore: "rgba(11, 22, 44, 0.9)",
      link: "rgba(11, 22, 44, 0.18)",
      linkTeal: "rgba(0, 122, 138, 0.28)",
    };

    function resize() {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    function seed() {
      particles.length = 0;
      const area = width * height;
      const count = Math.min(Math.floor((area / 16000) * density), 130);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          r: Math.random() * 1.6 + 0.6,
        });
      }
    }

    function step() {
      ctx.clearRect(0, 0, width, height);

      // Update positions
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        // Mouse attraction
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 140 * 140) {
            const f = 0.0009;
            p.vx += dx * f;
            p.vy += dy * f;
          }
        }
        // Velocity damp
        p.vx = Math.max(-0.6, Math.min(0.6, p.vx * 0.995));
        p.vy = Math.max(-0.6, Math.min(0.6, p.vy * 0.995));
      }

      // Draw links
      const linkDist = 130;
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkDist) {
            const alpha = 1 - dist / linkDist;
            ctx.strokeStyle =
              dist < 60
                ? `rgba(0, 122, 138, ${0.35 * alpha})`
                : `rgba(11, 22, 44, ${0.18 * alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        ctx.beginPath();
        ctx.fillStyle = COLORS.node;
        ctx.arc(p.x, p.y, p.r + 1.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = COLORS.nodeCore;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(step);
    }

    function onMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    }
    function onLeave() {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    }
    function onVisibility() {
      if (document.hidden) {
        cancelAnimationFrame(rafRef.current);
      } else {
        rafRef.current = requestAnimationFrame(step);
      }
    }

    resize();
    rafRef.current = requestAnimationFrame(step);
    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="molecular-canvas"
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    />
  );
}
